import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  LinearProgress,
  Typography,
  Alert,
} from "components/mui";
import { useNotificationProvider } from "hooks";
import {
  CreateTemplate,
  UpdateTemplate,
  GetEmailTemplates,
} from "api/canmail/CANmailAPI";

/**
 * "Save as Template" dialog for the Email Sender.
 *
 * Captures the current email content as a new template. If the chosen name
 * already exists it does not silently create an indistinguishable duplicate —
 * it asks the user to either overwrite the existing template or pick a new name.
 */
function SaveTemplateModal({
  open,
  onClose,
  editorType,
  textContent,
  dropContent,
  renderedHtml,
  onSaved,
}) {
  const { notifySuccess, notifyError } = useNotificationProvider();

  const [templateName, setTemplateName] = useState("");
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  // The existing template a typed name collides with (drives the overwrite step).
  const [collision, setCollision] = useState(null);

  useEffect(() => {
    if (open) {
      setTemplateName("");
      setSaving(false);
      setCollision(null);
      // Load existing names so we can detect duplicates before creating (best-effort).
      GetEmailTemplates()
        .then((data) => setTemplates(Array.isArray(data) ? data : []))
        .catch(() => setTemplates([]));
    }
  }, [open]);

  const isDrop = editorType === "drop";

  const buildPayload = (name) => ({
    template_name: name,
    template_content_text: isDrop ? "" : textContent || "",
    template_content_drop: isDrop ? dropContent || "" : "",
    rendered_html: renderedHtml || "",
    editor_type: isDrop ? "drop" : "text",
  });

  const hasContent = isDrop
    ? Boolean(dropContent && dropContent.trim())
    : Boolean(
        textContent &&
          textContent.trim() &&
          textContent !== "<p><br></p>" &&
          textContent !== "<p></p>"
      );

  const findCollision = (name) =>
    templates.find(
      (t) => (t.template_name || "").trim().toLowerCase() === name.trim().toLowerCase()
    );

  const doCreate = async (name) => {
    setSaving(true);
    try {
      const result = await CreateTemplate(buildPayload(name));
      notifySuccess(`Template "${name}" saved.`);
      onSaved?.(result);
      onClose();
    } catch (error) {
      console.error("[SaveTemplateModal] Error creating template:", error);
      notifyError("Error saving template, please try again.");
    } finally {
      setSaving(false);
    }
  };

  const doOverwrite = async () => {
    if (!collision) return;
    setSaving(true);
    try {
      await UpdateTemplate(collision.template_id, buildPayload(collision.template_name));
      notifySuccess(`Template "${collision.template_name}" updated.`);
      onSaved?.({
        template_id: collision.template_id,
        template_name: collision.template_name,
      });
      onClose();
    } catch (error) {
      console.error("[SaveTemplateModal] Error overwriting template:", error);
      notifyError("Error updating template, please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    const name = templateName.trim();
    if (!name) {
      notifyError("Template name is required.");
      return;
    }
    if (!hasContent) {
      notifyError("Add some email content before saving a template.");
      return;
    }
    const match = findCollision(name);
    if (match) {
      setCollision(match);
      return;
    }
    doCreate(name);
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {collision ? "Template name already exists" : "Save Template"}
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {collision ? (
          <>
            <Alert severity="warning">
              A template named <strong>{collision.template_name}</strong> already exists.
            </Alert>
            <Typography variant="body2">
              Overwrite the existing template with the current content, or go back and
              choose a different name.
            </Typography>
          </>
        ) : (
          <TextField
            autoFocus
            margin="dense"
            label="Template Name"
            fullWidth
            variant="outlined"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
            }}
          />
        )}
      </DialogContent>
      {saving && <LinearProgress />}
      <DialogActions>
        {collision ? (
          <>
            <Button onClick={() => setCollision(null)} disabled={saving} color="primary">
              Change name
            </Button>
            <Button onClick={doOverwrite} variant="contained" color="warning" disabled={saving}>
              {saving ? "Overwriting…" : "Overwrite"}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={saving} color="primary">
              Cancel
            </Button>
            <Button onClick={handleSave} variant="contained" disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default SaveTemplateModal;
